import React, { useEffect, useState } from 'react';
import { contentApi } from '../../services/contentApi';
import Button from '../../components/UI/Button';
import ActionStatus from '../../components/UI/ActionStatus';
import Input from '../../components/UI/Input';
import { useAutoClear } from '../../hooks/useAutoClear';

const PROVIDERS = [
  { name: 'OpenAI', apiUrl: 'https://api.openai.com/v1/' },
  { name: 'Groq', apiUrl: 'https://api.groq.com/openai/v1/' },
  { name: 'Grok', apiUrl: 'https://api.x.ai/v1/' },
  { name: 'Gemini', apiUrl: 'https://generativelanguage.googleapis.com/v1beta/' },
] as const;

type LlmModel = {
  id: number;
  providerId: number;
  name: string;
  isDefault: boolean;
  isEnabled: boolean;
  maxTokens: number;
};

type LlmProvider = {
  id: number;
  name: string;
  apiUrl?: string;
  hasApiKey?: boolean;
  apiKeyHint?: string;
  isEnabled: boolean;
  isDefault: boolean;
  models?: LlmModel[];
};

const emptyProvider = {
  id: undefined as number | undefined,
  name: 'OpenAI',
  apiUrl: PROVIDERS[0].apiUrl,
  apiKey: '',
  clearApiKey: false,
  isEnabled: true,
  isDefault: true,
};

const LlmConfigPage: React.FC = () => {
  const [providers, setProviders] = useState<LlmProvider[]>([]);
  const [form, setForm] = useState(emptyProvider);
  const [modelName, setModelName] = useState('');
  const [modelTokens, setModelTokens] = useState('4096');
  const [modelId, setModelId] = useState<number | undefined>(undefined);
  const [modelProviderId, setModelProviderId] = useState<number | undefined>(undefined);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useAutoClear(message, setMessage, 5000);
  useAutoClear(error, setError, 10000);

  const load = () => contentApi.llmProviders()
    .then((r: { data?: { providers?: LlmProvider[] } }) => setProviders(r.data?.providers || []))
    .catch(() => undefined);

  useEffect(() => { load(); }, []);

  const fail = (err: unknown, fallback: string) => {
    setMessage(null);
    const response = err as { response?: { data?: { message?: string } }; message?: string };
    setError(response.response?.data?.message || response.message || fallback);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <h1 className="text-3xl font-bold">LLM Configuration</h1>
      <p className="mt-2 text-gray-600">
        OpenAI, Groq, Grok, and Gemini share this configuration. The enabled default model is used by the editor AI action and by durable content generation.
        API keys are stored encrypted and are not shown again. A blank key on edit keeps the current key. If no key is saved here, the server configuration for that provider is used.
      </p>

      <form className="mt-6 space-y-3" onSubmit={async (e) => {
        e.preventDefault();
        setError(null);
        try {
          await contentApi.saveProvider({
            name: form.name,
            apiUrl: form.apiUrl,
            apiKey: form.apiKey,
            clearApiKey: form.clearApiKey,
            isEnabled: form.isEnabled,
            isDefault: form.isDefault,
          }, form.id);
          setMessage(form.id ? 'Provider updated.' : 'Provider saved.');
          setForm({ ...emptyProvider, name: form.name, apiUrl: form.apiUrl, isDefault: false });
          load();
        } catch (err: unknown) {
          fail(err, 'Could not save the provider.');
        }
      }}>
        <label className="block text-sm font-semibold text-gray-900" htmlFor="pname">
          Provider
          <select
            id="pname"
            className="mt-1.5 w-full rounded-md border-2 border-gray-300 bg-white px-3 py-2.5"
            value={form.name}
            onChange={(e) => {
              const name = e.target.value;
              const known = PROVIDERS.find((item) => item.name === name);
              setForm((current) => ({ ...current, name, apiUrl: known?.apiUrl || current.apiUrl }));
            }}
          >
            {PROVIDERS.map((item) => <option key={item.name} value={item.name}>{item.name}</option>)}
          </select>
        </label>
        <Input id="purl" label="API URL" value={form.apiUrl} onChange={(e) => setForm({ ...form, apiUrl: e.target.value })} />
        <Input id="pkey" label="API key" type="password" value={form.apiKey} onChange={(e) => setForm({ ...form, apiKey: e.target.value, clearApiKey: false })} autoComplete="off" />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.clearApiKey} onChange={(e) => setForm({ ...form, clearApiKey: e.target.checked, apiKey: e.target.checked ? '' : form.apiKey })} />
          Remove the stored API key
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.isEnabled} onChange={(e) => setForm({ ...form, isEnabled: e.target.checked })} />
          Enabled
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.isDefault} onChange={(e) => setForm({ ...form, isDefault: e.target.checked })} />
          Default provider
        </label>
        <div className="flex gap-2">
          <Button type="submit">{form.id ? 'Update provider' : 'Add provider'}</Button>
          {form.id && (
            <button type="button" className="rounded-md border border-gray-300 px-4 py-2 text-sm" onClick={() => setForm(emptyProvider)}>
              Cancel edit
            </button>
          )}
        </div>
        <ActionStatus message={message} error={error} />
      </form>

      <ul className="mt-8 space-y-4">
        {providers.map((provider) => (
          <li key={provider.id} className="rounded-lg border p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-medium">
                  {provider.name} {provider.isDefault ? '(default)' : ''} {!provider.isEnabled ? '(disabled)' : ''}
                </p>
                <p className="text-sm text-gray-500">{provider.apiUrl}</p>
                <p className="text-sm text-gray-500">
                  {provider.hasApiKey ? `Key ${provider.apiKeyHint || 'saved'}` : 'No key stored. Server configuration is used when present.'}
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                  onClick={() => setForm({
                    id: provider.id,
                    name: provider.name,
                    apiUrl: provider.apiUrl || '',
                    apiKey: '',
                    clearApiKey: false,
                    isEnabled: provider.isEnabled,
                    isDefault: provider.isDefault,
                  })}
                >
                  Edit
                </button>
                <button
                  type="button"
                  className="rounded-md border border-red-200 px-3 py-1.5 text-sm text-red-700"
                  onClick={async () => {
                    if (!window.confirm(`Delete ${provider.name} and its models?`)) return;
                    setError(null);
                    try {
                      await contentApi.deleteProvider(provider.id);
                      setMessage('Provider deleted.');
                      if (form.id === provider.id) setForm(emptyProvider);
                      load();
                    } catch (err: unknown) {
                      fail(err, 'Could not delete the provider.');
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
            <ul className="mt-3 space-y-2">
              {(provider.models || []).map((model) => (
                <li key={model.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-gray-50 px-3 py-2 text-sm">
                  <span>
                    {model.name} · {model.maxTokens} tokens {model.isDefault ? '· default' : ''} {!model.isEnabled ? '· disabled' : ''}
                  </span>
                  <span className="flex gap-2">
                    <button
                      type="button"
                      className="text-indigo-700"
                      onClick={() => {
                        setModelProviderId(provider.id);
                        setModelId(model.id);
                        setModelName(model.name);
                        setModelTokens(String(model.maxTokens));
                      }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="text-red-700"
                      onClick={async () => {
                        if (!window.confirm(`Delete model ${model.name}?`)) return;
                        setError(null);
                        try {
                          await contentApi.deleteModel(model.id);
                          setMessage('Model deleted.');
                          load();
                        } catch (err: unknown) {
                          fail(err, 'Could not delete the model.');
                        }
                      }}
                    >
                      Delete
                    </button>
                  </span>
                </li>
              ))}
            </ul>
            <form className="mt-3 grid gap-2 sm:grid-cols-[1fr_140px_auto]" onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              const editing = modelProviderId === provider.id ? modelId : undefined;
              const tokens = Number(modelTokens);
              try {
                const existing = provider.models?.find((item) => item.id === editing);
                await contentApi.saveModel({
                  providerId: provider.id,
                  name: modelName,
                  isDefault: existing ? existing.isDefault : true,
                  isEnabled: existing ? existing.isEnabled : true,
                  maxTokens: Number.isFinite(tokens) && tokens > 0 ? tokens : 4096,
                }, editing);
                setMessage(editing ? 'Model updated.' : 'Model saved as the default.');
                setModelName('');
                setModelId(undefined);
                setModelProviderId(undefined);
                load();
              } catch (err: unknown) {
                fail(err, 'Could not save the model.');
              }
            }}>
              <Input id={`m-${provider.id}`} label="Model" value={modelProviderId === provider.id ? modelName : ''} onChange={(e) => { setModelProviderId(provider.id); setModelName(e.target.value); }} />
              <Input id={`t-${provider.id}`} label="Max tokens" value={modelProviderId === provider.id ? modelTokens : '4096'} onChange={(e) => { setModelProviderId(provider.id); setModelTokens(e.target.value); }} />
              <div className="sm:pt-7">
                <Button type="submit">{modelProviderId === provider.id && modelId ? 'Update model' : 'Add default model'}</Button>
              </div>
            </form>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default LlmConfigPage;
